import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../services/api/api';
import { useRegisterPageMeta } from '../../contexts/PageMetaContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Download,
  TrendingUp,
  TrendingDown,
  Home,
  Apartment,
  AttachMoney,
  People,
  CalendarToday,
  FilterList,
  Refresh,
  Assessment,
  BarChart,
  PieChart,
} from '@mui/icons-material';
import DataTable from '../../components/UI/DataTable';
import PageHeader from '../../components/UI/PageHeader';
import OwnerStatusChip from '../../components/Owner/OwnerStatusChip';
import { OwnerPage, OwnerStatStrip } from '../../components/Owner';
import { ownerPrimaryButtonSx, portalOutlinedButtonSx } from '../../theme/designTokens';
import useOwnerSoftRefresh from '../../hooks/useOwnerSoftRefresh';
import { fetchProperties } from '../../store/slices/propertySlice';
import { formatMoney } from '../../utils/formatMoney';

const Analytics = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { properties } = useSelector((state) => state.properties);
  const { units } = useSelector((state) => state.units);
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dashboard Summary State
  const [dashboardSummary, setDashboardSummary] = useState(null);
  
  // Property Analytics State
  const [selectedProperty, setSelectedProperty] = useState('');
  const [propertyAnalytics, setPropertyAnalytics] = useState(null);
  
  // Payment Analytics State
  const [paymentFilters, setPaymentFilters] = useState({
    property_id: '',
    unit_id: '',
    start_date: null,
    end_date: null
  });
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  
  // Monthly Report State
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  
  // Export Dialog State
  const [exportDialog, setExportDialog] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    export_type: 'all',
    property_id: '',
    unit_id: '',
    start_date: null,
    end_date: null
  });

  useEffect(() => {
    dispatch(fetchProperties());
    loadDashboardSummary();
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 1 && properties.length > 0) {
      const propertyId = selectedProperty || properties[0].id;
      if (!selectedProperty) {
        setSelectedProperty(propertyId);
      }
      loadPropertyAnalytics(propertyId);
    }
    if (activeTab === 2) {
      loadPaymentAnalytics();
    }
    if (activeTab === 3) {
      loadMonthlyReport();
    }
  }, [activeTab, properties.length]);

  const loadDashboardSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/dashboard-summary');
      setDashboardSummary(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load dashboard summary');
    } finally {
      setLoading(false);
    }
  };

  const loadPropertyAnalytics = async (propertyId) => {
    try {
      setLoading(true);
      const response = await api.get(`/analytics/property/${propertyId}`);
      setPropertyAnalytics(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load property analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (paymentFilters.property_id) params.append('property_id', paymentFilters.property_id);
      if (paymentFilters.unit_id) params.append('unit_id', paymentFilters.unit_id);
      if (paymentFilters.start_date) params.append('start_date', paymentFilters.start_date.toISOString().split('T')[0]);
      if (paymentFilters.end_date) params.append('end_date', paymentFilters.end_date.toISOString().split('T')[0]);
      
      const response = await api.get(`/analytics/payments?${params}`);
      setPaymentAnalytics(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load payment analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (paymentFilters.property_id) params.append('property_id', paymentFilters.property_id);
      
      const response = await api.get(`/analytics/monthly-report/${reportYear}/${reportMonth}?${params}`);
      setMonthlyReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load monthly report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      params.append('export_type', exportFilters.export_type);
      if (exportFilters.property_id) params.append('property_id', exportFilters.property_id);
      if (exportFilters.unit_id) params.append('unit_id', exportFilters.unit_id);
      if (exportFilters.start_date) params.append('start_date', exportFilters.start_date.toISOString().split('T')[0]);
      if (exportFilters.end_date) params.append('end_date', exportFilters.end_date.toISOString().split('T')[0]);
      
      const response = await api.get(`/analytics/export/excel?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rental_analytics_${exportFilters.export_type}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setExportDialog(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (amount) => formatMoney(amount, 'UGX');

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const unitDetailColumns = [
    {
      id: 'unit_number',
      label: 'Unit Number',
      getSearchValue: (row) => row.unit_number,
    },
    {
      id: 'status',
      label: 'Status',
      render: (unit) => <OwnerStatusChip status={unit.status} />,
    },
    {
      id: 'monthly_rent',
      label: 'Monthly Rent',
      render: (unit) => formatCurrency(unit.monthly_rent),
    },
    { id: 'bedrooms', label: 'Bedrooms' },
    { id: 'bathrooms', label: 'Bathrooms' },
    {
      id: 'tenant_name',
      label: 'Tenant',
      getSearchValue: (row) => row.tenant_name || 'Available',
      render: (unit) => unit.tenant_name || 'Available',
    },
  ];

  const paymentDetailColumns = [
    {
      id: 'tenant_name',
      label: 'Tenant',
      getSearchValue: (row) => row.tenant_name,
    },
    {
      id: 'unit_number',
      label: 'Unit',
      getSearchValue: (row) => row.unit_number,
    },
    {
      id: 'property_name',
      label: 'Property',
      getSearchValue: (row) => row.property_name,
    },
    {
      id: 'amount',
      label: 'Amount',
      render: (payment) => formatCurrency(payment.amount),
    },
    { id: 'payment_date', label: 'Date' },
    {
      id: 'status',
      label: 'Status',
      render: (payment) => <OwnerStatusChip status={payment.status} />,
    },
  ];

  useRegisterPageMeta({
    title: 'Analytics',
    subtitle: 'Occupancy & collections',
  });

  useOwnerSoftRefresh(loadDashboardSummary);

  return (
    <OwnerPage>
        <PageHeader
          title="Analytics"
          subtitle="Occupancy, collections, and payment trends"
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadDashboardSummary}
                disabled={loading}
                sx={portalOutlinedButtonSx}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => setExportDialog(true)}
                sx={ownerPrimaryButtonSx}
              >
                Export Excel
              </Button>
            </Box>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        )}

        {/* Analytics Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab icon={<Assessment />} label="Dashboard Summary" />
            <Tab icon={<Home />} label="Property Analytics" />
            <Tab icon={<AttachMoney />} label="Payment Analytics" />
            <Tab icon={<CalendarToday />} label="Monthly Reports" />
          </Tabs>
        </Paper>

        {/* Dashboard Summary Tab */}
        {activeTab === 0 && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : dashboardSummary ? (
              <>
              <OwnerStatStrip
                sx={{ mb: 3 }}
                loading={loading}
                stats={[
                  { title: 'Properties', value: dashboardSummary.total_properties, icon: <Home /> },
                  { title: 'Units', value: dashboardSummary.total_units, icon: <Apartment /> },
                  {
                    title: 'Occupancy',
                    value: formatPercentage(dashboardSummary.occupancy_rate),
                    icon: <TrendingUp />,
                  },
                  {
                    title: 'Collection rate',
                    value: formatPercentage(dashboardSummary.collection_rate),
                    icon: <AttachMoney />,
                  },
                ]}
              />
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Rent Collection Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Monthly Rent:</Typography>
                        <Typography variant="h6">{formatCurrency(dashboardSummary.total_monthly_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="success.main">Collected:</Typography>
                        <Typography color="success.main">{formatCurrency(dashboardSummary.collected_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="warning.main">Pending:</Typography>
                        <Typography color="warning.main">{formatCurrency(dashboardSummary.pending_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="error.main">Overdue:</Typography>
                        <Typography color="error.main">{formatCurrency(dashboardSummary.overdue_rent)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Unit Status Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Occupied Units:</Typography>
                        <Typography variant="h6">{dashboardSummary.occupied_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Available Units:</Typography>
                        <Typography variant="h6">{dashboardSummary.available_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Total Units:</Typography>
                        <Typography variant="h6">{dashboardSummary.total_units}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              </>
            ) : (
              <Alert severity="info">
                No data available. Please check your properties and units.
              </Alert>
            )}
          </Box>
        )}

        {/* Property Analytics Tab */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth sx={{ maxWidth: 400 }}>
                <InputLabel>Select Property</InputLabel>
                <Select
                  value={selectedProperty}
                  onChange={(e) => {
                    setSelectedProperty(e.target.value);
                    if (e.target.value) {
                      loadPropertyAnalytics(e.target.value);
                    }
                  }}
                  label="Select Property"
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            {!loading && properties.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Add a property first to see property-level analytics.
              </Alert>
            )}

            {!loading && properties.length > 0 && !propertyAnalytics && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Select a property above to view analytics.
              </Alert>
            )}

            {propertyAnalytics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {propertyAnalytics.property_name}
                      </Typography>
                      <Typography color="textSecondary" gutterBottom>
                        {propertyAnalytics.property_address}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Units:</Typography>
                        <Typography variant="h6">{propertyAnalytics.total_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Occupied Units:</Typography>
                        <Typography variant="h6">{propertyAnalytics.occupied_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Available Units:</Typography>
                        <Typography variant="h6">{propertyAnalytics.available_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Occupancy Rate:</Typography>
                        <Typography variant="h6">{formatPercentage(propertyAnalytics.occupancy_rate)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Financial Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Monthly Rent:</Typography>
                        <Typography variant="h6">{formatCurrency(propertyAnalytics.total_monthly_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="success.main">Collected:</Typography>
                        <Typography color="success.main">{formatCurrency(propertyAnalytics.collected_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="warning.main">Pending:</Typography>
                        <Typography color="warning.main">{formatCurrency(propertyAnalytics.pending_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="error.main">Overdue:</Typography>
                        <Typography color="error.main">{formatCurrency(propertyAnalytics.overdue_rent)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <DataTable
                    columns={unitDetailColumns}
                    rows={propertyAnalytics.units || []}
                    title="Units"
                    emptyTitle="No units"
                    emptyDescription="No units for this property."
                    emptyIcon={Apartment}
                    getRowId={(row) => row.unit_id}
                    searchPlaceholder="Search by unit number or tenant…"
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {/* Payment Analytics Tab */}
        {activeTab === 2 && (
          <Box>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Payment Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={paymentFilters.property_id}
                      onChange={(e) => setPaymentFilters({ ...paymentFilters, property_id: e.target.value })}
                      label="Property"
                    >
                      <MenuItem value="">All Properties</MenuItem>
                      {properties.map((property) => (
                        <MenuItem key={property.id} value={property.id}>
                          {property.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={paymentFilters.start_date ? paymentFilters.start_date.toISOString().split('T')[0] : ''}
                    onChange={(e) => setPaymentFilters({ ...paymentFilters, start_date: e.target.value ? new Date(e.target.value) : null })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={paymentFilters.end_date ? paymentFilters.end_date.toISOString().split('T')[0] : ''}
                    onChange={(e) => setPaymentFilters({ ...paymentFilters, end_date: e.target.value ? new Date(e.target.value) : null })}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    onClick={loadPaymentAnalytics}
                    disabled={loading}
                    sx={{ height: '56px' }}
                  >
                    Apply Filters
                  </Button>
                </Grid>
              </Grid>
            </Paper>
            
            {!loading && paymentAnalytics && paymentAnalytics.summary?.total_payments === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No payment records match these filters yet. Record rent payments under Payments to see them here.
              </Alert>
            )}

            {paymentAnalytics && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Payment Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Payments:</Typography>
                        <Typography variant="h6">{paymentAnalytics.summary.total_payments}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Amount:</Typography>
                        <Typography variant="h6">{formatCurrency(paymentAnalytics.summary.total_amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="success.main">Completed:</Typography>
                        <Typography color="success.main">{formatCurrency(paymentAnalytics.summary.completed_amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="warning.main">Pending:</Typography>
                        <Typography color="warning.main">{formatCurrency(paymentAnalytics.summary.pending_amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="error.main">Overdue:</Typography>
                        <Typography color="error.main">{formatCurrency(paymentAnalytics.summary.overdue_amount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Completion Rate:</Typography>
                        <Typography variant="h6">{formatPercentage(paymentAnalytics.summary.completion_rate)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <DataTable
                    columns={paymentDetailColumns}
                    rows={paymentAnalytics.payments || []}
                    title="Payments"
                    emptyTitle="No payments"
                    emptyDescription="No records match filters."
                    emptyIcon={AttachMoney}
                    getRowId={(row) => row.payment_id}
                    searchPlaceholder="Search by tenant, unit, or property…"
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {/* Monthly Reports Tab */}
        {activeTab === 3 && (
          <Box>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Monthly Report Filters
        </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={reportYear}
                      onChange={(e) => setReportYear(e.target.value)}
                      label="Year"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={reportMonth}
                      onChange={(e) => setReportMonth(e.target.value)}
                      label="Month"
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                        <MenuItem key={month} value={month}>
                          {new Date(2024, month - 1).toLocaleString('default', { month: 'long' })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={paymentFilters.property_id}
                      onChange={(e) => setPaymentFilters({ ...paymentFilters, property_id: e.target.value })}
                      label="Property"
                    >
                      <MenuItem value="">All Properties</MenuItem>
                      {properties.map((property) => (
                        <MenuItem key={property.id} value={property.id}>
                          {property.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    onClick={loadMonthlyReport}
                    disabled={loading}
                    sx={{ height: '56px' }}
                  >
                    Generate Report
                  </Button>
                </Grid>
              </Grid>
      </Paper>
            
            {!loading && monthlyReport?.message && (
              <Alert severity="info" sx={{ mb: 2 }}>{monthlyReport.message}</Alert>
            )}

            {monthlyReport && !monthlyReport.message && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {monthlyReport.month_name} Report
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Properties:</Typography>
                        <Typography variant="h6">{monthlyReport.total_properties}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Units:</Typography>
                        <Typography variant="h6">{monthlyReport.total_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Occupied Units:</Typography>
                        <Typography variant="h6">{monthlyReport.occupied_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Available Units:</Typography>
                        <Typography variant="h6">{monthlyReport.available_units}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Occupancy Rate:</Typography>
                        <Typography variant="h6">{formatPercentage(monthlyReport.occupancy_rate)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Financial Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Total Monthly Rent:</Typography>
                        <Typography variant="h6">{formatCurrency(monthlyReport.total_monthly_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="success.main">Collected:</Typography>
                        <Typography color="success.main">{formatCurrency(monthlyReport.collected_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="warning.main">Pending:</Typography>
                        <Typography color="warning.main">{formatCurrency(monthlyReport.pending_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="error.main">Overdue:</Typography>
                        <Typography color="error.main">{formatCurrency(monthlyReport.overdue_rent)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Collection Rate:</Typography>
                        <Typography variant="h6">{formatPercentage(monthlyReport.collection_rate)}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {/* Export Dialog */}
        <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Export Analytics Data</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Export Type</InputLabel>
                  <Select
                    value={exportFilters.export_type}
                    onChange={(e) => setExportFilters({ ...exportFilters, export_type: e.target.value })}
                    label="Export Type"
                  >
                    <MenuItem value="all">All Data</MenuItem>
                    <MenuItem value="properties">Properties Only</MenuItem>
                    <MenuItem value="units">Units Only</MenuItem>
                    <MenuItem value="payments">Payments Only</MenuItem>
                    <MenuItem value="tenants">Tenants Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Property</InputLabel>
                  <Select
                    value={exportFilters.property_id}
                    onChange={(e) => setExportFilters({ ...exportFilters, property_id: e.target.value })}
                    label="Property"
                  >
                    <MenuItem value="">All Properties</MenuItem>
                    {properties.map((property) => (
                      <MenuItem key={property.id} value={property.id}>
                        {property.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={exportFilters.start_date ? exportFilters.start_date.toISOString().split('T')[0] : ''}
                  onChange={(e) => setExportFilters({ ...exportFilters, start_date: e.target.value ? new Date(e.target.value) : null })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={exportFilters.end_date ? exportFilters.end_date.toISOString().split('T')[0] : ''}
                  onChange={(e) => setExportFilters({ ...exportFilters, end_date: e.target.value ? new Date(e.target.value) : null })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialog(false)}>Cancel</Button>
            <Button onClick={handleExport} variant="contained" disabled={loading}>
              {loading ? 'Exporting...' : 'Export Excel'}
            </Button>
          </DialogActions>
        </Dialog>
    </OwnerPage>
  );
};

export default Analytics;